import moment from 'moment';
import uuid from 'uuid/v1';

import { getCurrencyValue } from 'utils/accommodations-and-activities/prepareAccommodationAndActivityData';

import { PRICES_PER_DAY } from '../../pages/create-accommodation/constants';
import {WEEKLY_BASE, WEEK_DAYS,HOURLY_LIST} from 'constants/base';

const WEEKDAYS = ['MON', 'TUE', 'WED', 'THU', 'FRI'];
const WEEKENDS = ['SAT', 'SUN'];


export const getReceivedRentalItemsData = data => {
  const generalForm = getGeneralInfo(data).generalForm;

  const descriptionForm = {
    description: data.description,
    included: data.included,
    notIncluded: data.notIncluded,
    cancellation: data.cancellation
  };

  const prices = getPrices(data);
  const rentedOutDatesData  = getRentedOutDates(data);

  const addOnsForm = {
    addOns: data.addOns.map(item => ({
      id: parseInt(item.id),
      title: item.title,
      description: item.description,
      coverPhoto: {
        image: item.photo
      },
      USD: getCurrencyValue(item.prices, 'USD'),
      EUR: getCurrencyValue(item.prices, 'EUR'),
      NOK: getCurrencyValue(item.prices, 'NOK'),
      isCreated: true
    }))
  };

  const secondLanguageForms = _getSecondLanguagesForm(data, addOnsForm);

  return {
    generalForm,
    descriptionForm,
    ...prices,
    rentedOutDatesForm: rentedOutDatesData,
    addOnsForm,
    ...secondLanguageForms,
  };
};

export const getGeneralInfo = data => {
    
  return {
    generalForm: {
      id: parseInt(data.id),
      productCode: data.productCode,
      title: data.title,
      coverPhoto: {
        image: data.photo
      },
      itemType: data.itemsType.id,
      status: data.status.id,
      availableUnits: data.availableUnits,
      minDays:data.minDays,
      maxDays:data.maxDays
    }
  };
};

const _getSecondLanguagesForm = (data, addOnsForm) => {
  const secondLanguage = 'Norwegian';
  const secondLanguageForms = {
    [`generalForm-${secondLanguage}`]: {},
    [`descriptionForm-${secondLanguage}`]: {}
  };

  if (data.titleNo) {
    secondLanguageForms[`generalForm-${secondLanguage}`] = {
      title: data.titleNo
    };

    secondLanguageForms[`descriptionForm-${secondLanguage}`] = {
      description: data.descriptionNo,
      included: data.includedNo,
      notIncluded: data.notIncludedNo,
      cancellation: data.cancellationNo,
      
    };

    addOnsForm.addOns = addOnsForm.addOns.map((item, index) => ({
      ...item,
      [secondLanguage]: {
        id: parseInt(item.id),
        title: data.addOns[index]['titleNo'],
        description: data.addOns[index]['descriptionNo']
      }
    }));
  }

  return secondLanguageForms;
};

const pricesFixedForm = (data) => {
  
  if (!Array.isArray(data?.prices) || data.prices.length === 0) {
    return PRICES_PER_DAY;
  }
  
  const apiValues = data.prices.reduce((acc, price) => {
    if (price.type === 'FIXED' && price.currency?.currency) {
      acc[price.currency.currency] = String(price.value ?? '0');
    }
    return acc;
  }, {});
  
  return { ...PRICES_PER_DAY, ...apiValues };
};


const pricesDailyForm = (data) => {

 const normalizeDayName = (name) => {

  if (!name) return ''; 
  if (typeof name !== 'string') return ''; // Extra safety
  const lower = name.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
};

    if (!Array.isArray(data?.prices) || data.prices.length === 0) {
    return {
      days: WEEK_DAYS.map(day => ({
        id: uuid(),
        day,
        type: 'day',
        ...PRICES_PER_DAY
      }))
    };
  }
  const prices = {};

  if (data?.prices?.length) {
    data.prices.forEach(price => {
      const rawDay = typeof price.day === 'string' ? price.day : price.day?.name;
      const dayName = normalizeDayName(rawDay);
      const currency = price.currency?.currency;

      if (!dayName || !currency) return;

      if (!prices[dayName]) {
        prices[dayName] = {};
      }

      prices[dayName][currency] = String(price.value ?? '0');
    });

    return {
      days: WEEK_DAYS.map(day => ({
        id: uuid(),
        day,
        type: 'day',
        USD: prices[day]?.USD ?? '0',
        EUR: prices[day]?.EUR ?? '0',
        NOK: prices[day]?.NOK ?? '0',
      }))
    };
  }
};


const pricesWeeklyForm = data => {
  if (!data?.prices || !Array.isArray(data.prices)) {
    return {
      weekList: WEEKLY_BASE.map(type => ({
        id: uuid(),
        type: type,
        days: type === 'weekday' ? WEEKDAYS : WEEKENDS,
        ...PRICES_PER_DAY
      }))
    };
  }

  const groupedByType = data.prices.reduce((acc, price) => {
    const type = price.type.toLowerCase();
    if (!acc[type]) acc[type] = [];
    acc[type].push(price);
    return acc;
  }, {});


  const weekList = WEEKLY_BASE.map(type => {
    const typeKey = type === 'weekday' ? 'weekday' : 'weekend';
    const pricesForType = groupedByType[typeKey] || [];
    
    const currencyValues = pricesForType.reduce((acc, price) => {
      const currency = price.currency.currency;
      acc[currency] = price.value.toString();
      return acc;
    }, { ...PRICES_PER_DAY });

    return {
      id: uuid(),
      type: type,
      days: type === 'weekday' ? WEEKDAYS : WEEKENDS,
      ...currencyValues
    };
  });

  return { weekList };
};


const pricesHourlyForm = data => {
  if (!Array.isArray(data?.hourlyPrices) || data.hourlyPrices.length === 0) {
    return {
      hourlyList: [
        {
          id: uuid(),
          time: 'Master Price',
          startTime: null,
          isMaster: true,
          minHours: data?.masterMinMaxHours?.minHours,
          maxHours: data?.masterMinMaxHours?.maxHours,
          ...PRICES_PER_DAY
        }
      ]
    };
  }

  const hourlyMap = {};
  const masterPriceEntry = {
    id: uuid(),
    time: 'Master Price',
    startTime: null,
    isMaster: true,
    minHours: data?.masterMinMaxHours?.minHours,
    maxHours: data?.masterMinMaxHours?.maxHours ,
    ...PRICES_PER_DAY
  };

  data.hourlyPrices.forEach(item => {
    const hourData = item.hour;
    const currency = item.currency.currency;
    const value = item.value;
    const displayHour = hourData.displayHour;

    if (displayHour === 'Master Price') {
      masterPriceEntry[currency] = value.toString();
      return;
    }

    const hourMatch = displayHour.match(/^(\d{1,2}):\d{2}$/);
    if (!hourMatch) {
      return;
    }

    const hour = parseInt(hourMatch[1]);

    if (isNaN(hour) || hour < 0 || hour > 23) {
      return;
    }

    const startTime = moment().set({
      hour: hour,
      minute: 0,
      second: 0,
      millisecond: 0
    });

    if (!hourlyMap[hour]) {
      hourlyMap[hour] = {
        id: uuid(),
        time: '',
        startTime: startTime,
        isMaster: false,
        minHours: '',
        maxHours: '',
        ...PRICES_PER_DAY
      };
    }

    hourlyMap[hour][currency] = value.toString();
  });

  const hourlyEntries = Object.values(hourlyMap).sort((a, b) => {
    return a.startTime - b.startTime;
  });

  const hourlyList = [];

  const hasMasterPrice = Object.keys(PRICES_PER_DAY).some(currency => {
    const value = masterPriceEntry[currency];
    return (
      value !== '0' &&
      value !== 0 &&
      value !== PRICES_PER_DAY[currency] &&
      value !== '' &&
      value != null
    );
  });

  if (hasMasterPrice) {
    hourlyList.push(masterPriceEntry);
  }

  hourlyList.push(...hourlyEntries);

  if (hourlyList.length === 0) {
    hourlyList.push({
      id: uuid(),
      time: 'Master Price',
      startTime: null,
      isMaster: true,
      minHours: data?.masterMinMaxHours?.minHours,
      maxHours: data?.masterMinMaxHours?.maxHours,
      ...PRICES_PER_DAY
    });
  }

  return {
    hourlyList: hourlyList
  };
};

const getRentedOutDates = (data) => {
  
  if (!data?.rentedOutDates || !Array.isArray(data.rentedOutDates)) {
    return { rentedOutList: [] };
  }
  const result = {
    rentedOutList: data.rentedOutDates.map((date, index) => {
  
      const mappedItem = {
        id: date.id || uuid(),
        dateRange: {
          startDate: moment(date.startDate),
          endDate: moment(date.endDate)
        },
        numberOfUnits: date.numberOfUnits ?? 0,
      };
      return mappedItem;
    }),
  };
  
  return result;
};

export const getReceivedRentalItemsBookingsDates = receivedAvailableUnitsData => {
  if (!receivedAvailableUnitsData.rentalItems.length) {
    return receivedAvailableUnitsData;
  }

  const receivedAvailableUnitsPerDate =
    receivedAvailableUnitsData.rentalItems[0]
      .availableUnitsPerDate;

  return {
    rentalItems: [
      {
        availableUnitsPerDate: receivedAvailableUnitsPerDate.map(
          item => ({
            ...item,
          })
        )
      }
    ]
  };
};


export const getPrices = data => {
  return {
    pricesFixedForm: pricesFixedForm(data),
    pricesDailyForm: pricesDailyForm(data),
    pricesWeeklyForm: pricesWeeklyForm(data),
    pricesHourlyForm: pricesHourlyForm(data)
  };
};